import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { getStateID, isStateRestricted } from 'harmony/toolbox/shipToUtils';

/**
 * @typedef {typeof import('widgets/Widget').default} Widget
 * @typedef {ReturnType<typeof import('widgets/wishlist/HarmonyAddToWishlistMixin').default>} HarmonyAddToWishlistMixin
 */

/**
 * @param {Widget} Widget Base widget for extending
 * @returns {typeof ProductSet} Product Set widget
 */
export default function (Widget) {
    /**
     * @category widgets
     * @subcategory product
     * @class ProductSet
     * @augments Widget
     * @classdesc Represents product set component with the next features:
     * 1. Scrolls page to the product list, once `Show the collection` button pressed.
     *
     * @example <caption>Example of product set component usage</caption>
     *
     * <div
     data-widget="productSet"
     ...
     >
     ...
     <div class="l-pdp" data-ref="productsContainer">
     ....
     </div>
     <div class="l-pdp-main m-product_set_buy_all">
     ....
     </div>
     </div>
     */
    class ProductSet extends Widget {
        prefs() {
            return {
                pid: '',
                selectedQuantity: '',
                showMinicartOnProductAdd: '',
                showAlertOnProductAdd: '',
                addToCartGlobalMsg: '',
                textNetworkError: '',
                ...super.prefs()
            };
        }

        get constants() {
            return {
                ALERT_SHOW: 'alert.show'
            };
        }

        init() {
            super.init();
            this.handleStateRestrictions();

            this.eventBus().on('shipto.restrictions.updated', 'handleStateRestrictions');
        }

        /**
         * @description If product set has unselected product variation,
         * show error message for all such products, scroll to the first one error and set focus first product with error
         * @returns {void}
         */
        handleUnselectedProduct() {
            /** @type {HTMLElement|undefined} */
            let unselectedProduct;

            this.eachChild((child) => {
                if (child.prefs().productSetItem && (!child.prefs().readyToOrder)) {
                    child.getById('addToCartMsg', (addToCartMsg) => {
                        const error = true;
                        const msg = child.prefs().textSelectOptions;

                        addToCartMsg.render(
                            'template',
                            { msg, error },
                            addToCartMsg.ref('container')
                        ).then(() => addToCartMsg.show());
                    });

                    if (!unselectedProduct) {
                        unselectedProduct = child.getById('productName', (productName) => productName.ref('titleLink').get());
                    }
                }
            });

            if (unselectedProduct) {
                unselectedProduct.focus();
                scrollWindowTo(unselectedProduct, true);
            }
        }

        /**
         * @description Returns product set data for the set items
         * @returns {Array} result
         */
        getProductSetItems(statID) {
            const productSetItems = [];
            const stateID = getStateID(statID);

            this.items[0]?.eachChild((child) => {
                if (child.prefs().productSetItem) {
                    let isOutOfStock = false;
                    let isRestricted = false;

                    child.getById('addToCart', (addToCart) => {
                        isOutOfStock = !!addToCart.prefs().isOutOfStock;

                        if (stateID) {
                            isRestricted = isStateRestricted(stateID, addToCart.prefs().shipToRestrictedStates);
                        }
                    });

                    productSetItems.push({
                        pid: child.prefs().currentProductID || child.prefs().pid,
                        quantity: child.prefs().selectedQuantity,
                        available: !isOutOfStock,
                        isRestricted: isRestricted,
                        readyToOrder: child.prefs().readyToOrder
                    });
                }
            });

            return productSetItems;
        }

        /**
         * @description Returns an array with products available for ordering
         * @param {Array} productSetItems - product set data for the set items
         * @returns {Array} result
         */
        getPidsObj(productSetItems) {
            return productSetItems.filter(setItem => setItem.available && !setItem.isRestricted).map((childProduct) => {
                return {
                    pid: childProduct.pid,
                    qty: childProduct.quantity
                };
            });
        }

        /**
         * @description On add all to cart button click handler
         * @param {any} button - button
         * @returns {void}
         */
        onAddAllToCart(button) {
            const productSetItems = this.getProductSetItems();
            const unselectedProduct = productSetItems.find(
                (setItem) => !setItem.readyToOrder && setItem.available
            );

            if (unselectedProduct) {
                this.handleUnselectedProduct();
                return;
            }

            const pidsObj = this.getPidsObj(productSetItems);
            const isFullProductSet = pidsObj.length === productSetItems.length;

            button.startProcess();

            const promise = submitFormJson(button.prefs().addToCartUrl, {
                pid: this.prefs().pid || button.prefs().pid,
                quantity: this.prefs().selectedQuantity || button.prefs().selectedQuantity,
                pidsObj: JSON.stringify(pidsObj)
            }).then(response => this.postAddAllToCart(response, isFullProductSet, pidsObj.length))
                .finally(() => {
                    button.stopProcess();
                }).catch(() => {
                    this.showAlertMessage(this.prefs().textNetworkError, true);
                });

            this.eventBus().emit('loader.start', promise);
        }

        /**
         * @description Handles a result of product set adding to cart
         * @param {object} response - response
         * @param {boolean} isFullProductSet - Identifies if all product set items are added
         * @param {number} productsCount - products count to be added to the cart
         * @returns {void}
         */
        postAddAllToCart(response) {
            if (!response.error && response.cart) {
                const cartModel = response.cart;

                // Hide mini cart pop up for CSC Agent
                if (response.isCSCAgent) {
                    cartModel.showMinicart = false;
                } else {
                    cartModel.showMinicart = this.prefs().showMinicartOnProductAdd;
                }

                this.showAlertMessage(this.prefs().accessibilityAlerts.addedtocart, false);
                this.eventBus().emit('product.added.to.cart', cartModel, this);
            } else {
                this.showAlertMessage(response.message, true);
            }
        }

        /**
         * @description Emits global alert
         * @param {string} message Text for an alert
         * @param {boolean} isError is error flag
         */
        showAlertMessage(message, isError = false) {
            this.eventBus().emit(this.constants.ALERT_SHOW, {
                accessibilityAlert: message,
                errorClass: isError
            });
        }

        /**
         * @description Handle update of shipping restrictions
         */
        handleStateRestrictions(statID) {
            const productSetItems = this.getProductSetItems(statID);
            const isAllChildRestricted = productSetItems.every((setItem) => setItem.isRestricted);
            const stateID = getStateID(statID);

            if (isAllChildRestricted) {
                const HarmonyAddToWishlistMixin = /** @type {HarmonyAddToWishlistMixin} */(this.getConstructor('productDetail'));

                this.eachChild(child => {
                    if (!(child instanceof HarmonyAddToWishlistMixin)) {
                        return;
                    }

                    child.handleStateRestrictions(stateID, isAllChildRestricted);

                    // handle ship to error
                    child.getById('shiptoerror', (shiptoerror) => {
                        shiptoerror.show();
                    });
                });
            }
        }
    }

    return ProductSet;
}
